import React, { useState, useEffect } from 'react';
import SEO from '@components/seo';
import { RichTextBlock } from 'prismic-reactjs';
import createRelativePosts from '@helpers/create-relative-posts';
import Image from '@components/image';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import renderModule from '@components/module';
import Layout from '@layouts/default-layout';
// import TagList from '@components/posts-list/tag-list/tag-list';
import Wrapper from '@components/wrapper.styled';
import {
  PageContainer,
  TopMeta,
  MetaContainer,
  DownloadContainer,
  DownloadTitle,
  DownloadButton,
  HeroImageContainer,
  PostContainer,
  SubMeta,
  TextSubMeta,
  PostBody,
  StyledPostDescription,
  AuthorName,
  PrefixAuthorName,
  // TagWrapper,
  TitleContainer,
  Title,
  IframedWrapper,
} from '@components/paragraphs-blog/paragraphs.shared.styled';
import RelativePosts from '@components/relative-posts';
import { PostsListProps } from '@components/posts-list/relative-posts-list';
import { CategoriesPost } from '@components/module/module.shared.styled';
import SharePost from '@components/share-post';
import generateId from '@helpers/generate-id';
import getFormatDate from '@helpers/get-format-date';

export interface CategoriesProps {
  category: {
    id: string;
    document: {
      data: {
        color: string;
        name: {
          text: string;
        };
      };
    };
  };
}

const postPageIdGenerator = generateId();

export interface PostProps {
  data: {
    prismicGeneralConfig: {
      data: {
        favicon?: {
          url?: string;
        };
      };
    };
    prismicPostPageConfig: {
      data: {
        title_for_relative_posts: {
          text: string;
        };
        body: [];
      };
    };
    currentPost: {
      edges: {
        node: {
          type: 'blog_post' | 'guides_post' | 'case_studies_post';
          uid: string;
          first_publication_date: string;
          tags: string[];
          data: {
            featured_image: {
              url: string;
              alt: string;
              gatsbyImageData: IGatsbyImageData;
            };
            date: string;
            title: {
              text: string;
            };
            author: string;
            body: [];
            content: {
              text: string;
              richText: RichTextBlock[];
            };
            embedded_media: {
              url: string;
            };
            case_study_pdf?: {
              url?: string;
            };
            // call_to_action: {
            //   document: {
            //     data: {
            //       image?: ImageProps;
            //       title?: {
            //         text: string;
            //       };
            //       description?: {
            //         text: string;
            //         richText: RichTextBlock[];
            //       };
            //       link: {
            //         url: string;
            //         target?: string;
            //       };
            //       link_label: string;
            //     };
            //   };
            // };
          };
        };
      }[];
    };
    relativePosts?: PostsListProps;
    restPosts?: PostsListProps;
  };
}

const Post = ({
  data: {
    currentPost,
    relativePosts,
    restPosts,
    prismicPostPageConfig,
    prismicGeneralConfig: {
      data: { favicon },
    },
  },
}: PostProps): JSX.Element | null => {
  if (!currentPost) return null;
  const [displayRelativePosts, setDisplayRelativePosts] = useState([] as PostsListProps['edges']);
  const post = currentPost.edges[0].node;
  const { title_for_relative_posts, body } = prismicPostPageConfig?.data || {};
  const headerImage = post.data.featured_image;
  const postDate = post.data.date || post.first_publication_date;
  const shouldShowDate = post.type !== 'case_studies_post' && Boolean(postDate);
  const shouldShowAuthor = Boolean(post.data.author && post.data.author !== 'wpengine');

  useEffect(() => {
    createRelativePosts(relativePosts, restPosts, displayRelativePosts, setDisplayRelativePosts);
  }, []);

  const getPostUrl = (): string => (typeof window !== 'undefined' ? window.location.href : '');

  return (
    <Layout>
      <SEO
        title={post.data.title.text || 'Blog'}
        description={post.data.content.text}
        faviconHref={favicon?.url || ''}
        image={post.data.featured_image}
      />
      <PageContainer>
        <CategoriesPost postsType={post.type} />
        {(post.data.title.text || post.data.content.text) && (
          <Wrapper>
            <PostContainer>
              <>
                <MetaContainer>
                  <TopMeta>
                    <SubMeta>
                      <TextSubMeta>
                        <AuthorName>
                          {shouldShowDate && `Published on ${getFormatDate(postDate)}`}

                          {shouldShowAuthor && (
                            <>
                              {shouldShowDate && <PrefixAuthorName> by </PrefixAuthorName>}
                              {post.data.author}
                            </>
                          )}
                        </AuthorName>
                      </TextSubMeta>
                    </SubMeta>
                    {post.type === 'case_studies_post' && post.data.case_study_pdf?.url && (
                      <DownloadContainer>
                        <DownloadTitle>Download Case Study</DownloadTitle>
                        <DownloadButton
                          href={post.data.case_study_pdf.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download PDF
                        </DownloadButton>
                      </DownloadContainer>
                    )}
                    {/* {post.tags.length > 0 && (
                      <TagWrapper>
                        <TagList tags={post.tags} blogUrl="/" />
                      </TagWrapper>
                    )} */}
                  </TopMeta>
                  <SharePost
                    textColor="dark"
                    url={getPostUrl()}
                    title={post.data.title.text}
                    description={post.data.content.text}
                  />
                </MetaContainer>
                <PostBody>
                  <TitleContainer>
                    {post.data.title.text && (
                      <Title
                        type="title"
                        fontSize="heading1"
                        stringText={post.data.title.text}
                        fontColor="dark"
                      />
                    )}
                  </TitleContainer>
                  {headerImage?.url && (
                    <HeroImageContainer>
                      <Image
                        gatsbyImageData={headerImage?.gatsbyImageData}
                        url={headerImage?.url}
                        alt={headerImage?.url}
                      />
                    </HeroImageContainer>
                  )}
                  {post.data.embedded_media?.url && (
                    <IframedWrapper>
                      <iframe
                        src={post.data.embedded_media.url}
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        title="Embedded Media"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                        }}
                      />
                    </IframedWrapper>
                  )}
                  <StyledPostDescription type="description" richText={post.data.content.richText} />
                </PostBody>
              </>
            </PostContainer>
          </Wrapper>
        )}

        {!!displayRelativePosts?.length && (
          <RelativePosts
            edges={displayRelativePosts}
            title={title_for_relative_posts?.text || ''}
            postsType={post.type}
          />
        )}
        {body?.map(
          (module) =>
            module && (
              <React.Fragment key={postPageIdGenerator.next().value}>
                {renderModule(module, post.first_publication_date)}
              </React.Fragment>
            )
        )}
      </PageContainer>
    </Layout>
  );
};

export default Post;
